// The Catalog (ADR-0047): the feature flags and the offered targets Core
// serves at GET /catalog, the same feed it quotes from, so a price on screen
// is the price charged. Pure helpers here; the fetch is cached once per page
// load because the catalog only changes with a deploy or a definitions reload.
import { useEffect, useState } from "react";
import { getCatalog, type Catalog, type CatalogTarget } from "../api/client";

export type { Catalog, CatalogTarget };

/** The targets one alias offers, shortest and slowest first. */
export function targetsFor(catalog: Catalog | undefined, alias: string): CatalogTarget[] {
  if (!catalog) return [];
  return catalog.targets
    .filter((t) => t.alias === alias)
    .slice()
    .sort((a, b) => a.fps - b.fps || a.seconds - b.seconds);
}

export function targetKey(t: Pick<CatalogTarget, "seconds" | "fps">): string {
  return `${t.seconds}s@${t.fps}`;
}

/** "1280×720 · 5 s · 24 fps" */
export function targetLabel(t: CatalogTarget): string {
  return `${t.width}×${t.height} · ${t.seconds} s · ${t.fps} fps`;
}

/** A wait as people say it: "45 s", "4 min", "1 h 5 min". */
export function formatWait(seconds: number): string {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds / 5) * 5)} s`;
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

/** Provider cost in minor units with one decimal, as cents: "11.7¢", "$1.40". */
export function formatCost(minor: number, currency: string): string {
  if (currency !== "USD_CENTS") return `${minor} ${currency}`;
  if (minor < 100) return `${minor.toFixed(1)}¢`;
  return `$${(minor / 100).toFixed(2)}`;
}

/** Price minus the cold cost, as a share of the price (the worst case: one
 * clip pays the whole cold start). */
export function coldMargin(t: CatalogTarget): number | undefined {
  const cost = t.estimate.cold_cost_minor;
  if (cost === undefined || t.price <= 0) return undefined;
  return (t.price - cost) / t.price;
}

let cached: Promise<Catalog> | undefined;

/** The catalog, fetched once per page load; undefined until it arrives or
 * when it fails (the views then behave as before the catalog existed). A
 * changed `key` (the signed-in user) retries a fetch that failed signed out. */
export function useCatalog(key?: unknown): Catalog | undefined {
  const [catalog, setCatalog] = useState<Catalog | undefined>(undefined);
  useEffect(() => {
    let live = true;
    cached ??= getCatalog();
    cached
      .then((c) => live && setCatalog(c))
      .catch(() => {
        cached = undefined;
      });
    return () => {
      live = false;
    };
  }, [key]);
  return catalog;
}

/** Tests only: forget the cached fetch. */
export function resetCatalogCache(): void {
  cached = undefined;
}

/** The one product the grade aliases are offered as (ADR-0047): the user
 * picks a resolution, a length and a frame rate, and the alias follows the
 * resolution. */
export const IMAGE_TO_VIDEO = "image-to-video";

export interface GradeChoice {
  grade: string;
  alias: string;
  width: number;
  height: number;
}

/** The grades among `aliases` the Catalog has targets for, smallest first. */
export function gradeChoices(catalog: Catalog | undefined, aliases: string[]): GradeChoice[] {
  if (!catalog) return [];
  const out = new Map<string, GradeChoice>();
  for (const t of catalog.targets) {
    if (aliases.includes(t.alias) && !out.has(t.alias)) {
      out.set(t.alias, { grade: t.grade, alias: t.alias, width: t.width, height: t.height });
    }
  }
  return [...out.values()].sort((a, b) => a.width * a.height - b.width * b.height);
}

/** The lengths one alias offers, and the frame rates at one length. */
export function secondsFor(catalog: Catalog | undefined, alias: string): number[] {
  return [...new Set(targetsFor(catalog, alias).map((t) => t.seconds))].sort((a, b) => a - b);
}
export function fpsFor(catalog: Catalog | undefined, alias: string, seconds: number): number[] {
  return [...new Set(targetsFor(catalog, alias).filter((t) => t.seconds === seconds).map((t) => t.fps))].sort((a, b) => a - b);
}
